import React from 'react';
import * as QRCode from 'qrcode';

import { MDText } from 'i18n-react';

import { Button } from '@motorway/mw-highway-code';
import { SIZE } from '@motorway/mw-highway-code/enums';

import { VehicleLookupState } from 'Utilities/vehicles/@types';

import { Modal } from 'Storybook/components/local/Modal/Modal';
import { StepList } from 'Storybook/components/local/StepList';

import { HowVerificationWorksModal } from '../../../HowVerificationWorksModal/HowVerificationWorksModal';
import LocalTexts from '../../UploadDocuments.texts.json';

import {
  getKeeperSteps,
  getSellerKeeperSteps,
  getSellerSteps,
} from './UploadDocumentsVerification.helpers';

import styles from './UploadDocumentsVerification.scss';

const LocalT = new MDText(LocalTexts.uploadDocumentsVerification);

type UploadDocumentsVerificationWrapperProps = {
  children?: React.ReactNode;
  closeModal: () => void;
  isFamilyOrFriendKeeper?: boolean;
  isModalOpen: boolean;
  keeperName?: string | null;
  renderButton: () => React.ReactNode;
  showFSLStep?: boolean;
  slug: string;
  token?: string | null;
  vehicle: VehicleLookupState;
};

type Step = { content: React.ReactNode; id: number };

const StepGroup = ({ description, steps, title }: {
  description?: React.ReactNode;
  steps: Step[];
  title: React.ReactNode;
}) => (
  <section className={styles.stepGroup}>
    <h5 className={styles.stepGroupTitle}>{title}</h5>
    {description && (
      <p data-thc-style-override className={styles.stepGroupDescription}>{description}</p>
    )}
    <StepList align="center" counter="hidden" steps={steps} />
  </section>
);

const QrCodeModal = ({ closeModal, slug, token, vehicle }: {
  closeModal: () => void;
  slug: string;
  token?: string | null;
  vehicle: VehicleLookupState;
}) => (
  <Modal contentClass={styles.confirmModal} dismissModal={closeModal} size={SIZE.SMALL}>
    <canvas
      ref={(node) => {
        if (!node) {
          return;
        }

        const url = new URL(`${globalThis.location.origin}/${vehicle.vrm}${slug}`);
        if (token) {
          url.searchParams.append('token', token);
        }
        url.searchParams.append('utm_medium', 'qr_code');

        QRCode.toCanvas(node, url.toString());
      }}
    />
    <h2>{LocalT.translate('incodeQrModal.title')}</h2>
    <p>{LocalT.translate('incodeQrModal.content')}</p>
    <Button fullWidth label={LocalT.translate('incodeQrModal.confirm')} onClick={closeModal} />
  </Modal>
);

export const UploadDocumentsVerificationWrapper = ({
  children = null,
  closeModal,
  isFamilyOrFriendKeeper = false,
  isModalOpen,
  keeperName = null,
  renderButton,
  showFSLStep = false,
  slug,
  token,
  vehicle,
}: UploadDocumentsVerificationWrapperProps) => {
  const keeperDescription = keeperName
    ? LocalT.translate('steps.keeperStep.descriptionWithName', { keeperName })
    : LocalT.translate('steps.keeperStep.descriptionGeneric');

  const renderStepGroups = () => {
    if (isFamilyOrFriendKeeper) {
      return (
        <>
          <StepGroup
            description={keeperDescription}
            steps={getKeeperSteps()}
            title={LocalT.translate('steps.keeperStep.title')}
          />
          <StepGroup
            steps={getSellerSteps({ showFSLStep })}
            title={LocalT.translate('steps.sellerStep.title')}
          />
        </>
      );
    }

    const sellerKeeperSteps = getSellerKeeperSteps({ showFSLStep });
    return (
      <StepGroup
        steps={sellerKeeperSteps}
        title={LocalT.translate('steps.justXSteps', { count: sellerKeeperSteps.length })}
      />
    );
  };

  return (
    <div>
      {isModalOpen && <QrCodeModal {...{ closeModal, slug, token, vehicle }} />}
      <div className={styles.uploadDocumentsVerification}>
        <div className={styles.summary}>
          <h2 className={styles.title}>{LocalT.translate('documentVerification')}</h2>
          <p data-thc-style-override className={styles.info}>
            {LocalT.translate(isFamilyOrFriendKeeper
              ? 'documentVerificationDescription'
              : 'documentVerificationDescriptionSellerKeeper')}
          </p>
          <HowVerificationWorksModal />
        </div>
        {renderStepGroups()}
        <div className={styles.buttonContainer}>
          {renderButton()}
          {children}
        </div>
      </div>
    </div>
  );
};
